import {
  getFileExtension,
  IdentityValidationDocumentName,
  IdentityValidationId,
} from '@contexts/IdentityValidation/domain';
import { AptunoError } from '@shared/models';
import { useEffect, useState } from 'react';
import { useDocumentActions } from '../store/documents.actions.context';
import { LoadingStatus } from './loading-status';
import { useUploadFileInexternal } from './use-upload-file-in-external';

type LoadFileData = {
  name: IdentityValidationDocumentName;
  identityValidationId: string;
  file: File;
};

const useUploadFile = () => {
  const {
    addDocument,
    documentAdded,
    error: addDocumentError,
  } = useDocumentActions().documentsCommandActions.useAddDocument();

  const {
    uploadFileInExternal,
    errors: uploadFileInExternalErrors,
    loaded: uploadedFileInExternal,
  } = useUploadFileInexternal();

  const {
    markIdentityValidationDocumentAsLoaded,
    identityValidationDocumentMarkedAsLoaded,
    error: markDocumentAsLoadError,
  } = useDocumentActions().documentsCommandActions.useMarkIdentityValidationDocumentAsLoaded();

  const [uploadingStatus, setUploadingStatus] = useState<LoadingStatus>(LoadingStatus.NOT_INITIALIZED);
  const [uploadFileData, setUploadFileData] = useState<LoadFileData | undefined>();

  useEffect(() => {
    if (uploadFileData?.file) {
      const documentToUpload = {
        identityValidationId: uploadFileData.identityValidationId,
        documentToAdd: uploadFileData.name,
        fileExtension: getFileExtension(uploadFileData?.file.type),
      };

      addDocument(documentToUpload);
    }
  }, [uploadFileData?.file]);

  useEffect(() => {
    if (documentAdded && uploadFileData?.name) {
      uploadFileInExternal(uploadFileData);
    }
  }, [documentAdded]);

  useEffect(() => {
    if (uploadedFileInExternal && uploadFileData) {
      markIdentityValidationDocumentAsLoaded({
        identityValidationId: new IdentityValidationId(uploadFileData.identityValidationId),
        documentName: uploadFileData.name,
      });
    }
  }, [uploadedFileInExternal]);

  useEffect(() => {
    if (documentAdded && uploadedFileInExternal && identityValidationDocumentMarkedAsLoaded) {
      setUploadingStatus(() => LoadingStatus.LOADED);
    }
  }, [uploadedFileInExternal, documentAdded, identityValidationDocumentMarkedAsLoaded]);

  return {
    uploadFile: (data: LoadFileData) => {
      setUploadingStatus(() => LoadingStatus.LOADING);
      setUploadFileData(data);
    },
    uploadingStatus: uploadingStatus,
    uploadedFileName: uploadFileData?.name,
    uploadedFile: uploadFileData?.file,
    errors: <AptunoError[]>(
      [addDocumentError, ...uploadFileInExternalErrors, markDocumentAsLoadError].filter((error) => !!error)
    ),
  };
};

export { useUploadFile };
